
/** Very old dependency without ts typings **/
import { VMoney } from 'v-money'
import { debounce } from 'lodash-es'
import { ref, computed, watch } from '@nuxtjs/composition-api'
import { useI18n } from '~/helpers/hooks/usei18n'

import TwoThumbsSlider from '~/components/atoms/TwoThumbsSlider.vue'

const CURRENCIES = {
  EUR: ' €',
  USD: ' $'
}

export default {
  components: { TwoThumbsSlider },
  directives: {
    money: VMoney
  },
  props: {
    value: {
      type: Array,
      default: null
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    },
    type: {
      type: String,
      default: 'number',
      validator: type => ['number', 'currency'].includes(type)
    }
  },
  emits: ['input', 'input:instant'],
  setup (props, ctx) {
    const i18n = useI18n()

    const moneyFromEl = ref(null)
    const moneyToEl = ref(null)
    const minValue = ref<number>(props.value?.[0] ?? props.min)
    const maxValue = ref<number>(props.value?.[1] ?? props.max)
    const minInputValue = ref<number>(props.value?.[0] ?? props.min)
    const maxInputValue = ref<number>(props.value?.[1] ?? props.max)

    const inputsType = computed<string>(() => props.type === 'number' ? 'input' : 'money')
    const _moneyFormat = computed<string>(() => (
      CURRENCIES[i18n.getNumberFormat(i18n.locale)?.currency?.currency ?? 'EUR']
    ))
    const moneyOptions = computed(() => (
      props.type === 'number'
        ? null
        : {
            decimal: '.',
            thousands: '',
            suffix: _moneyFormat.value,
            precision: 0,
            masked: false
          }
    ))
    const range = computed<number[]>({
      get () {
        return normalizeMinMax(minValue.value, maxValue.value)
      },
      set (val: number[]) {
        const normalized = normalizeMinMax(val[0], val[1])
        minValue.value = minInputValue.value = normalized[0]
        maxValue.value = maxInputValue.value = normalized[1]
      }
    })

    const handleMinInputChange = debounce((min: number | string): void => {
      min = parseFloat(`${min}`.replace(_moneyFormat.value, ''))
      minValue.value = Math.max(min, props.min)
      updateInputs()
      moneyFromEl.value?.$el.blur()
    }, 700)
    const handleMaxInputChange = debounce((max: number): void => {
      max = parseFloat(`${max}`.replace(_moneyFormat.value, ''))
      maxValue.value = Math.min(max, props.max)
      updateInputs()
      moneyToEl.value?.$el.blur()
    }, 700)

    watch(() => props.value, (newVal: number[]) => {
      if (Array.isArray(newVal)) {
        if (!newVal.length) {
          range.value = [props.min, props.max]
        } else {
          range.value = newVal
        }
      }
    }, { immediate: true })

    function updateInputs (): void {
      ctx.emit('input', range.value)
    }

    function handleSliderChange (): void {
      ctx.emit('input:instant', [minValue.value, maxValue.value])
    }

    function normalizeMinMax (min: number, max: number): number[] {
      return [Math.min(min, max), Math.max(min, max)]
    }

    return {
      moneyFromEl,
      moneyToEl,
      inputsType,
      minValue,
      maxValue,
      minInputValue,
      maxInputValue,
      range,
      moneyOptions,
      handleSliderChange,
      handleMinInputChange,
      handleMaxInputChange
    }
  }
}
